/*
 * The Calendar's controls, month grid and week grid (piece 12, the Calendar
 * page). Every dimension and colour reads a token; the four event-type
 * families are the ones the token file declares, so a chip's colour and its
 * legend cannot drift apart.
 */

/* 🔨 The page fills the content region and the grid takes what the rows above
   it leave (decision 0329). 💰 The grid ended under its last row and the rest
   of the region stood empty (product owner, 2026-09-12). */
.page {
  display: flex;
  flex-direction: column;
  /* 🔨 The page step every other page uses (decision 0601): the wrapper carried no gap,
     so the actions row sat against the grid (product owner, 2026-09-20). */
  gap: var(--space-4);
  min-height: 100%;
}

.calendar {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}

/* ---- The banner's controls (decision 0329): the scroller, Today and the scale, spread across the banner's width ---- */

.viewControls {
  flex: 1 1 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-1) var(--space-2);
  min-width: 0;
}

@media (--bp-md) {
  .viewControls {
    gap: var(--space-2) var(--space-3);
  }
}

/* ---- The row directly under the banner: Team on the left, + Add Event and Show cancelled on the right ---- */

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  padding: 0 0 var(--space-2);
}

.actionsRight {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  margin-left: auto;
}

/* The lines beneath the controls: the legend and, in Overlay, the people (figure 65). */
.toolbarRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-1);
}

.hint {
  color: var(--text-muted);
  font-size: var(--font-size-small);
  font-style: italic;
}

/* Today sits on the banner's gradient: outlined in the banner's own text colour, filled on hover. */
.today {
  height: var(--button-height-small);
  padding: 0 var(--space-4);
  border: 1px solid var(--on-status-fill);
  border-radius: var(--radius-small);
  background: transparent;
  color: var(--on-status-fill);
  font: inherit;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
}

.today:hover { background: var(--banner-control-wash); }

/* The scroller: Prev, the month and year, Next. */
.strip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
}

/* The month and year heading between Prev and Next (decisions 0278 and 0329).
   💰 At the title size it filled a phone's width on its own, so Today and the
   Month/Week toggle each took a row of their own (product owner, 2026-09-14).
   It reads at the body size under md, where the three groups share two short
   rows instead of three tall ones. */
.heading {
  margin: 0;
  min-width: 0;
  padding: 0 var(--space-1);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-bold);
  color: var(--on-status-fill);
  white-space: nowrap;
}

@media (--bp-md) {
  .heading {
    padding: 0 var(--space-2);
    font-size: var(--font-size-title);
  }
}

.stripMonths {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1);
  border: 1px solid var(--surface-2);
  border-radius: var(--radius-stadium);
}

.month {
  min-width: 44px;
  height: var(--button-height-small);
  padding: 0 var(--space-3);
  border: 0;
  border-radius: var(--radius-stadium);
  background: transparent;
  color: var(--text-muted);
  font: inherit;
  font-size: var(--font-size-small);
  cursor: pointer;
}

.month[aria-current='true'] {
  background: var(--selection-bg);
  color: var(--navy-primary);
  font-weight: var(--font-weight-bold);
}

.step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--button-height-small);
  height: var(--button-height-small);
  border: 0;
  border-radius: var(--radius-small);
  background: transparent;
  color: var(--on-status-fill);
  cursor: pointer;
}

.step:hover { background: var(--banner-control-wash); }

.today:focus-visible,
.month:focus-visible,
.step:focus-visible,
.scale:focus-visible,
.chip:focus-visible,
.block:focus-visible,
.more:focus-visible,
.teamButton:focus-visible,
.viewOnly:focus-visible,
.close:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* Month / Week, the segmented pair, at the banner's right; the pressed half is filled in the banner's text colour. */
.scales {
  display: inline-flex;
  padding: var(--space-half);
  border-radius: var(--radius-stadium);
  background: var(--banner-control-wash);
}

.scale {
  height: var(--button-height-small);
  padding: 0 var(--space-4);
  border: 0;
  border-radius: var(--radius-stadium);
  background: transparent;
  color: var(--on-status-fill);
  font: inherit;
  font-size: var(--font-size-small);
  cursor: pointer;
}

.scale[aria-pressed='true'] {
  background: var(--on-status-fill);
  color: var(--navy-primary);
  font-weight: var(--font-weight-semibold);
}

/* The legend names what each chip colour means, above the grid (figure 65); in Overlay it names each person (figure 67). */
.legendTitle {
  color: var(--section-label-color);
  font-size: var(--section-label-size);
  font-weight: var(--section-label-weight);
  letter-spacing: var(--section-label-tracking);
  text-transform: uppercase;
}

.legend {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-4);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-semibold);
}

.legendItem {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

.legendItem[data-person='true'] {
  font-size: var(--font-size-caption);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.swatch {
  width: var(--list-row-status-dot-size);
  height: var(--list-row-status-dot-size);
  border-radius: var(--radius-stadium);
  background: var(--swatch-color, var(--navy-primary));
}

.swatch[data-legend='inPerson'] { background: var(--cal-event-in-person-text); }
.swatch[data-legend='online'] { background: var(--cal-event-online-text); }
.swatch[data-legend='call'] { background: var(--cal-event-call-text); }
.swatch[data-legend='none'] { background: var(--cal-event-none-text); }
.swatch[data-legend='pto'] { background: var(--cal-event-pto-text); }
.swatch[data-legend='workOrder'] { background: var(--cal-event-work-order-text); }

/* ---- The Team dropdown (figure 66) ---- */

.team {
  position: relative;
}

.teamButton {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: var(--button-height-medium);
  padding: 0 var(--space-3);
  border: 1px solid var(--border);
  border-radius: var(--radius-small);
  background: var(--surface);
  color: var(--text);
  font: inherit;
  font-size: var(--font-size-small);
  cursor: pointer;
}

.teamPanel {
  position: absolute;
  top: calc(100% + var(--space-1));
  left: 0;
  z-index: 20;
  min-width: calc(var(--calendar-card-width) * 1.5);
  max-height: 60vh;
  overflow-y: auto;
  padding: var(--space-2) 0;
  border: 1px solid var(--surface-2);
  border-radius: var(--radius-medium);
  background: var(--surface);
  box-shadow: var(--calendar-card-shadow);
}

.teamRow {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
}

.teamRow[data-mine='true'] {
  border-left: var(--tree-bar-width) solid var(--navy-primary);
  border-bottom: 1px solid var(--surface-2);
}

.teamText {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  gap: var(--space-half);
}

/* The caption sits beneath the name, indented past the box (figure 66). */
.teamCaption {
  padding-left: var(--space-6);
  color: var(--text-muted);
  font-size: var(--font-size-caption);
  font-style: italic;
}

.teamCaption[data-shown='true'] {
  color: var(--person-color, var(--text-muted));
}

.teamHeading {
  padding: var(--space-2) var(--space-3) 0;
  color: var(--section-label-color);
  font-size: var(--section-label-size);
  font-weight: var(--section-label-weight);
  letter-spacing: var(--section-label-tracking);
  text-transform: uppercase;
}

.viewOnly {
  flex: none;
  height: var(--button-height-small);
  padding: 0 var(--space-3);
  border: 1px solid var(--navy-primary);
  border-radius: var(--radius-small);
  background: var(--surface);
  color: var(--navy-primary);
  font: inherit;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
}

.viewOnly[aria-pressed='true'] {
  background: var(--navy-primary);
  color: var(--on-status-fill);
}

/* ---- The month grid ---- */

.weekdays,
.week {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.weekdays {
  border-bottom: 1px solid var(--surface-2);
}

.weekday {
  padding: var(--space-2) var(--space-2);
  color: var(--section-label-color);
  font-size: var(--section-label-size);
  font-weight: var(--section-label-weight);
  letter-spacing: var(--section-label-tracking);
  text-align: center;
  text-transform: uppercase;
}

/* The month takes the region's remaining height and shares it across its rows (decision 0329); a row never shrinks below its cell minimum. */
.grid {
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--surface-2);
  border-radius: var(--radius-medium);
  background: var(--surface);
}

/* A row is a day band plus the bars that cross it, so a multi-day event is one
   element spanning columns rather than a chip repeated per cell. */
.week {
  position: relative;
  flex: 1 1 auto;
  border-bottom: 1px solid var(--surface-2);
}

.week:last-child { border-bottom: 0; }

.cell {
  min-height: var(--calendar-cell-height);
  padding: var(--space-1) var(--space-1) 0;
  border-right: 1px solid var(--surface-2);
}

.cell:last-child { border-right: 0; }

.cell[data-in-month='false'] { background: var(--surface-disabled); }

.dayNumber {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: var(--calendar-day-marker);
  height: var(--calendar-day-marker);
  padding: 0 var(--space-1);
  border-radius: var(--radius-stadium);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-semibold);
  color: var(--text);
}

.cell[data-in-month='false'] .dayNumber { color: var(--text-muted); }

/**
 * 🔒 Today's whole cell is ringed in navy, and its day number stays filled
 * (decision 0453). The filled marker alone was 22px of colour in a 92px cell,
 * and the product owner read the day as unmarked. The ring is inset, so it
 * costs the grid no width and moves nothing beside it.
 */
.cell[data-today='true'] {
  box-shadow: inset 0 0 0 var(--calendar-today-ring-width) var(--navy-primary);
}

/**
 * 🔒 A holiday is a background band, not an event (piece 15; decision 1720):
 * the day's ground takes the holiday tint and its name stands beside the day
 * number, above the chips, so it never takes one of the day's two chip rows.
 */
.cell[data-holiday='true'],
.compactCell[data-holiday='true'],
.weekGrid .column[data-holiday='true'],
.weekHead[data-holiday='true'] { background-color: var(--calendar-holiday-bg); }

.cellHead {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
}

.holidayName {
  display: block;
  min-width: 0;
  overflow: hidden;
  color: var(--calendar-holiday-text);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-semibold);
  letter-spacing: normal;
  text-overflow: ellipsis;
  text-transform: none;
  white-space: nowrap;
}

.dayPanelTitle .holidayName { display: inline; margin-left: var(--space-2); }

/* Today is a filled navy marker, the one place the grid names a day. */
.dayNumber[data-today='true'] {
  background: var(--navy-primary);
  color: var(--on-status-fill);
  font-weight: var(--font-weight-bold);
}

/* The bars sit over the day band, one row of them per stacked event. */
.bars {
  position: absolute;
  inset: var(--calendar-bars-top) 0 0 0;
  grid-auto-rows: var(--calendar-chip-height);
  align-content: start;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: var(--space-1);
  padding: 0 var(--space-1);
  pointer-events: none;
}

.chip {
  grid-row: 1;
  display: block;
  overflow: hidden;
  min-width: 0;
  height: var(--calendar-chip-height);
  padding: 0 var(--space-2);
  border: 0;
  border-radius: var(--radius-small);
  font: inherit;
  font-size: var(--font-size-caption);
  line-height: var(--calendar-chip-height);
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  pointer-events: auto;
}

/* 🔨 One rule for every shape an event draws in — the month chip, the week
   block and the week's multi-day band — so a type's colour cannot be right in
   one of the three and absent in another. 💰 The band carried the type
   attribute and no rule answered it, so a multi-day event in the week view drew
   unfilled (product owner, 2026-09-20). */
.chip[data-kind='inPerson'],
.block[data-kind='inPerson'],
.band[data-kind='inPerson'] { background: var(--cal-event-in-person-bg); color: var(--cal-event-in-person-text); }
.chip[data-kind='online'],
.block[data-kind='online'],
.band[data-kind='online'] { background: var(--cal-event-online-bg); color: var(--cal-event-online-text); }
.chip[data-kind='call'],
.block[data-kind='call'],
.band[data-kind='call'] { background: var(--cal-event-call-bg); color: var(--cal-event-call-text); }
.chip[data-kind='pto'],
.block[data-kind='pto'],
.band[data-kind='pto'] { background: var(--cal-event-pto-bg); color: var(--cal-event-pto-text); }
.chip[data-kind='workOrder'],
.block[data-kind='workOrder'],
.band[data-kind='workOrder'] { background: var(--cal-event-work-order-bg); color: var(--cal-event-work-order-text); }
.chip[data-kind='none'],
.block[data-kind='none'],
.band[data-kind='none'] { background: var(--cal-event-none-bg); color: var(--cal-event-none-text); }

/* In Overlay every event carries its owner's colour on its left bar, the way figure 67 draws it. */
.chip[data-owned='true'],
.block[data-owned='true'],
.band[data-owned='true'] {
  border-left: var(--tree-bar-width) solid var(--owner-color, var(--navy-primary));
}

/* A cancelled meeting is drawn as cancelled, never as an ordinary one (decision 0262). */
.chip[data-cancelled='true'],
.block[data-cancelled='true'],
.band[data-cancelled='true'] {
  text-decoration: line-through;
  opacity: var(--control-disabled-opacity);
}

.more {
  grid-row: 2;
  padding: 0 var(--space-2);
  border: 0;
  background: transparent;
  color: var(--text-muted);
  font: inherit;
  font-size: var(--font-size-caption);
  text-align: left;
  cursor: pointer;
  pointer-events: auto;
}

/* ---- The Expanded card, anchored to the day it was opened from (figure 68) ---- */

/* The card is a child of the grid frame and its offsets are measured from the
   chip it was opened from, so it keeps its own width and overlaps whatever is
   beside it. 💰 Placed from the day's column number alone it opened at the top
   of the column whatever hour the block sat at (product owner, 2026-09-14), and
   before that a clamp to the column squeezed it to a strip (2026-09-10). */
.card {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 20;
  width: var(--calendar-card-width);
  max-width: calc(100% - var(--space-2));
}

/* The Expanded card stands alone, no frame and no × of its own: a click outside closes it (product owner, 2026-09-10). */
.card > * {
  box-shadow: var(--calendar-card-shadow);
}

.cardHead {
  display: flex;
  justify-content: flex-end;
  padding: var(--space-1) var(--space-1) 0;
}

.close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--button-height-small);
  height: var(--button-height-small);
  border: 0;
  border-radius: var(--radius-small);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}

.readOnly {
  padding: 0 var(--space-3) var(--space-2);
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

/* ---- The week view: hourly slots (piece 12) ---- */

/* The week takes the region's remaining height too (decision 0329): the heads
   and the bands keep their own height, and the hour rows share the rest, never
   shrinking below an hour's minimum. */
.weekGrid {
  position: relative;
  flex: 1 1 auto;
  /* 🔨 The day is drawn midnight to midnight and the band scrolls, so every hour is reachable. */
  overflow-y: auto;
  display: grid;
  grid-template-columns: var(--calendar-hour-gutter) repeat(7, minmax(0, 1fr));
  grid-template-rows: auto minmax(0, 1fr);
  border: 1px solid var(--surface-2);
  border-radius: var(--radius-medium);
  background: var(--surface);
}

.weekGrid[data-has-bands='true'] {
  grid-template-rows: auto auto minmax(0, 1fr);
}

.weekHead {
  padding: var(--space-2);
  border-bottom: 1px solid var(--surface-2);
  color: var(--section-label-color);
  font-size: var(--section-label-size);
  font-weight: var(--section-label-weight);
  letter-spacing: var(--section-label-tracking);
  text-align: center;
  text-transform: uppercase;
}

.weekHead[data-today='true'] { color: var(--navy-primary); }

/* The multi-day bands across the top of the week's columns (decision 0296). */
.bandGutter,
.bandCell {
  border-bottom: 1px solid var(--surface-2);
  padding: var(--space-half) 0;
}

.bandCell {
  display: flex;
  flex-direction: column;
  gap: var(--space-half);
  border-left: 1px solid var(--surface-2);
  min-height: var(--calendar-chip-height);
}

.band {
  display: block;
  min-height: var(--calendar-chip-height);
  margin: 0 var(--space-half);
  padding: var(--space-half) var(--space-2);
  border: 0;
  border-radius: var(--radius-small);
  font: inherit;
  font-size: var(--font-size-caption);
  line-height: var(--line-height-tight);
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.band[data-continues-before='true'] {
  margin-left: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.band[data-continues-after='true'] {
  margin-right: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.hours {
  display: flex;
  flex-direction: column;
}

/* An hour is a share of the row's height, an hour's minimum at the least; the column's lines are drawn at the same share, so a block placed by its fraction lands on its hour. */
.hour {
  flex: 1 1 var(--calendar-hour-height);
  box-sizing: border-box;
  min-height: var(--calendar-hour-height);
  padding: 0 var(--space-2);
  border-bottom: 1px solid var(--surface-2);
  color: var(--text-muted);
  font-size: var(--font-size-caption);
  text-align: right;
  font-variant-numeric: var(--figures);
}

.column {
  position: relative;
  min-height: calc(var(--calendar-hour-height) * var(--calendar-week-hours));
  border-left: 1px solid var(--surface-2);
  background-image: linear-gradient(to bottom, var(--surface-2) 1px, transparent 1px);
  background-size: 100% calc(100% / var(--calendar-week-hours));
}

.column[data-today='true'] { background-color: var(--surface-disabled); }

.block {
  position: absolute;
  left: var(--space-1);
  right: var(--space-1);
  /* A five-minute event is still one readable line: the block never shrinks below the chip height (product owner, 2026-09-11). */
  min-height: var(--calendar-chip-height);
  overflow: hidden;
  padding: var(--space-1) var(--space-2);
  border: 0;
  border-radius: var(--radius-small);
  font: inherit;
  font-size: var(--font-size-caption);
  line-height: var(--line-height-tight);
  text-align: left;
  cursor: pointer;
}

/* 🔨 The Travel Buffer is its own block: lighter fill, a dashed outline, its own label (piece 12). */
.block[data-travel='true'] {
  border: 1px dashed var(--cal-event-in-person-text);
  background: var(--surface);
  color: var(--cal-event-in-person-text);
  font-style: italic;
  cursor: default;
}

.blockTitle {
  display: block;
  overflow: hidden;
  font-weight: var(--font-weight-semibold);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* A tall block shows its booking: the range and place, then the notes and the attendees, each line clipped rather than wrapped past the block. */
.block[data-block-detail='1'],
.block[data-block-detail='2'] {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-half);
  text-align: left;
}

.blockLine {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.85;
}

.blockNotes {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  max-width: 100%;
  font-weight: var(--font-weight-regular);
  opacity: 0.85;
}

.blockAttendees {
  display: block;
  margin-top: var(--space-half);
}

/* ---- The calendar under the small breakpoint (decision 0266): dots, a day list and an inline card ---- */

/* The prose lines are the first thing a phone cannot afford. */
.compact .hint { display: none; }

/* ---- The compact calendar's own chrome (product owner, 2026-09-14) ----
   💰 On an iPhone 13 the bands above the grid took about 690px of an 844px
   screen — a two-line banner, the month row, the Month/Week row, the Team row,
   the Add Event row, and a legend carrying its own heading over two lines — and
   three weeks of the month were all that showed. Every band below is the same
   content at the height a phone can spare, so the grid gets the rest. */

/* The legend keeps its meaning and loses its heading: the swatch and its words
   say what the heading said, and the heading cost a whole row. */
.compact .legendTitle { display: none; }

.compact .legend {
  gap: var(--space-1) var(--space-3);
  font-size: var(--font-size-caption);
}

.compact .toolbarRow {
  gap: var(--space-2);
  padding: var(--space-1) 0;
}

/* Team, + Add Event and Show cancelled read as one band rather than three. */
.compact .actions {
  gap: var(--space-2);
  padding: 0 0 var(--space-1);
}

.compact .actionsRight {
  gap: var(--space-3);
}

.compact .teamButton {
  height: var(--button-height-small);
  padding: 0 var(--space-2);
  font-size: var(--font-size-caption);
}


/* The compact month keeps its own height; the day list beneath it takes the rest. */
.compactGrid { flex: none; }

.compactGrid .week { flex: none; }

.compactGrid .weekday { padding: var(--space-1) 0; }

.compactCell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  min-height: var(--calendar-cell-height-compact);
  padding: var(--space-1) 0;
  border-top: 0;
  border-left: 0;
  border-bottom: 0;
  background: var(--surface);
  font: inherit;
  cursor: pointer;
}

.compactCell[data-in-month='false'] { background: var(--surface-disabled); }
.compactCell[data-selected='true'] { box-shadow: inset 0 0 0 2px var(--navy-primary); }

.dots {
  display: inline-flex;
  align-items: center;
  gap: var(--space-half);
  min-height: var(--calendar-dot-size);
}

.dot {
  width: var(--calendar-dot-size);
  height: var(--calendar-dot-size);
  border-radius: var(--radius-stadium);
  background: var(--cal-event-none-text);
}

.dot[data-kind='inPerson'] { background: var(--cal-event-in-person-text); }
.dot[data-kind='online'] { background: var(--cal-event-online-text); }
.dot[data-kind='call'] { background: var(--cal-event-call-text); }
.dot[data-kind='pto'] { background: var(--cal-event-pto-text); }
.dot[data-kind='workOrder'] { background: var(--cal-event-work-order-text); }

.dotMore {
  font-size: var(--font-size-caption);
  line-height: 1;
  color: var(--text-muted);
}

.dayPanel {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-top: var(--space-3);
  padding: var(--space-3);
  border: 1px solid var(--surface-2);
  border-radius: var(--radius-medium);
  background: var(--surface);
}

.dayPanelHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}

.dayPanelTitle {
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-semibold);
}

/* A row is the chip at full width: readable time, readable subject, the type's colour on the bar. */
.dayRow {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-half);
  padding: var(--space-2) var(--space-3);
  border: 0;
  border-left: var(--tree-bar-width) solid var(--cal-event-none-text);
  border-radius: var(--radius-small);
  background: var(--cal-event-none-bg);
  color: var(--cal-event-none-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.dayRow[data-kind='inPerson'] { border-left-color: var(--cal-event-in-person-text); background: var(--cal-event-in-person-bg); color: var(--cal-event-in-person-text); }
.dayRow[data-kind='online'] { border-left-color: var(--cal-event-online-text); background: var(--cal-event-online-bg); color: var(--cal-event-online-text); }
.dayRow[data-kind='call'] { border-left-color: var(--cal-event-call-text); background: var(--cal-event-call-bg); color: var(--cal-event-call-text); }
.dayRow[data-kind='pto'] { border-left-color: var(--cal-event-pto-text); background: var(--cal-event-pto-bg); color: var(--cal-event-pto-text); }
.dayRow[data-kind='workOrder'] { border-left-color: var(--cal-event-work-order-text); background: var(--cal-event-work-order-bg); color: var(--cal-event-work-order-text); }
.dayRow[data-owned='true'] { border-left-color: var(--owner-color, var(--navy-primary)); }
.dayRow[data-cancelled='true'] { text-decoration: line-through; opacity: var(--control-disabled-opacity); }

.dayRowTime {
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-semibold);
}

.dayRowTitle {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-semibold);
  color: var(--text);
}

